<template>
  <div class="overflow-x-auto">
    <DataTable
      v-model:expandedRows="expandedRows"
      :value="periods"
      size="small"
      showGridlines
      scrollable
      dataKey="key"
      :dt="COMPACT_TABLE_DT"
      :pt="EXPANSION_PT"
      class="text-sm"
      tableClass="min-w-[760px]"
      data-testid="txn-analytics-comparison-table"
    >
      <Column expander style="width: 3rem" />
      <Column field="label" :header="t('messages.transactions.analytics.table.period')">
        <template #body="{ data }">
          <span class="whitespace-nowrap">{{ data.label }}</span>
        </template>
      </Column>
      <Column field="n" :header="t('messages.transactions.analytics.table.n')" :pt="rightPT">
        <template #body="{ data }">
          <span class="tabular-nums">{{ formatEt(data.total?.count ?? data.n, 0) }}</span>
        </template>
      </Column>
      <Column
        v-for="column in VALUE_COLUMNS"
        :key="column.field"
        :field="column.field"
        :header="t(`messages.transactions.analytics.table.${column.labelKey}`)"
        :pt="rightPT"
      >
        <template #body="{ data }">
          <span class="tabular-nums">{{ data.total ? formatCell(data.total, column.field, column.dp) : '–' }}</span>
        </template>
      </Column>
      <Column field="change_pct_median" :header="t('messages.transactions.analytics.table.change')" :pt="rightPT">
        <template #body="{ data }">
          <span
            class="tabular-nums whitespace-nowrap"
            :class="changeClass(data.change_pct_median)"
            :data-testid="`txn-analytics-change-${data.key}`"
          >
            {{ changeText(data.change_pct_median) }}
          </span>
        </template>
      </Column>

      <template #expansion="{ data }">
        <div class="p-2 bg-surface-50">
          <PriceStatisticsTable :period="data" :table="table" />
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatEt, formatCell } from '@/services/numberFormat'
import { COMPACT_TABLE_DT, EXPANSION_PT } from '@/services/analyticsTable'
import PriceStatisticsTable from './PriceStatisticsTable.vue'

/**
 * RED-310 — "Mitu ajavahemikku" comparison table. One row per period
 * (oldest first, as the server orders them), KOKKU figures per row and the
 * median change vs the previous row (▲ green / ▼ red / "–" on the first or
 * a suppressed period). Expanding a row shows that period's band table.
 */
defineProps({
  periods: { type: Array, required: true },
  table: { type: Object, default: null },
})

const { t } = useI18n()

const VALUE_COLUMNS = [
  { field: 'area_mean', labelKey: 'area_mean', dp: 1 },
  { field: 'sum_total', labelKey: 'sum_total', dp: 0 },
  { field: 'ppm_median', labelKey: 'ppm_median', dp: 2 },
  { field: 'ppm_mean', labelKey: 'ppm_mean', dp: 2 },
  { field: 'ppm_sd', labelKey: 'sd', dp: 2 },
]

const expandedRows = ref({})

const rightPT = {
  columnHeaderContent: { class: 'justify-end text-right' },
  bodyCell: { class: 'text-right whitespace-nowrap' },
}

const changeClass = value => {
  if (value === null || value === undefined) return 'text-gray-400'
  if (value > 0) return 'text-green-600'
  if (value < 0) return 'text-red-600'
  return ''
}

const changeText = value => {
  if (value === null || value === undefined) return '–'
  const arrow = value > 0 ? '▲ ' : value < 0 ? '▼ ' : ''
  return `${arrow}${formatEt(Math.abs(value), 1)} %`
}
</script>
